import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { Film, Link2, User, FileText, AlertCircle, CheckCircle } from 'lucide-react';
import API from '../utils/api';
import { Section, Field } from '../components/FormPrimitives';

// Fallback shown until the admin-editable list loads (or if that request
// fails) — mirrors backend/src/routes/submissions.js's DEFAULT_GENRES,
// keep both in sync. The real list is admin-editable via Settings →
// Submission Genres, without a code change.
const DEFAULT_GENRES = ['Drama','Thriller','Comedy','Horror','Romance','Documentary','Animation','Experimental','Action','Sci-Fi','Fantasy','AI Film','Other'];
const LANGUAGES = ['Tamil','Hindi','Telugu','Malayalam','Kannada','English','Bengali','Marathi','Other'];

// Mirrors the allow-list enforced server-side in backend/src/routes/submissions.js — keep both in sync.
const ALLOWED_LINK_HOSTS = ['drive.google.com', 'docs.google.com', 'youtube.com', 'www.youtube.com', 'youtu.be', 'vimeo.com', 'player.vimeo.com'];
function isAllowedLink(url) {
  try {
    const host = new URL(url).hostname.toLowerCase();
    return ALLOWED_LINK_HOSTS.includes(host);
  } catch { return false; }
}

export default function Submit() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const editId = params.get('edit');
  const resubmitId = params.get('resubmit');

  const [seasonInfo, setSeasonInfo] = useState(null);
  const [submitToGeneral, setSubmitToGeneral] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [step, setStep] = useState('form'); // 'form' | 'review'
  const [fieldErrors, setFieldErrors] = useState({});
  const [genres, setGenres] = useState(DEFAULT_GENRES);
  const [form, setForm] = useState({
    title: '', genre: '', language: '', format: 'horizontal', episode_count: '', season_number: '', duration_hours: '', duration_mins: '', duration_secs: '',
    synopsis: '', film_link: '', film_link_password: '', trailer_link: '',
    director_name: '', director_phone: '', director_email: '', studio_name: '',
    notes_to_reviewers: '',
  });

  useEffect(() => {
    API.get('/api/submissions/active-season').then(r => setSeasonInfo(r.data)).catch(() => {});
    API.get('/api/submissions/genres').then(r => { if (r.data.genres?.length) setGenres(r.data.genres); }).catch(() => {});
    if (editId || resubmitId) {
      API.get('/api/submissions/my').then(r => {
        const sub = r.data.submissions.find(s => s.id === (editId || resubmitId));
        if (sub) setForm({
          title: sub.title || '', genre: sub.genre || '', language: sub.language || '', format: sub.format || 'horizontal',
          episode_count: sub.episode_count != null ? String(sub.episode_count) : '',
          season_number: sub.season_number != null ? String(sub.season_number) : '',
          duration_hours: sub.duration_minutes != null ? String(Math.floor(sub.duration_minutes / 60)) : '',
          duration_mins: sub.duration_minutes != null ? String(sub.duration_minutes % 60) : '',
          duration_secs: '',
          synopsis: sub.synopsis || '',
          film_link: sub.film_link || '', film_link_password: sub.film_link_password || '', trailer_link: sub.trailer_link || '',
          director_name: sub.director_name || '', director_phone: sub.director_phone || '',
          director_email: sub.director_email || '', studio_name: sub.studio_name || '',
          notes_to_reviewers: sub.notes_to_reviewers || '',
        });
      });
    }
  }, [editId, resubmitId]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const REQUIRED_FIELDS = [
    ['title', 'Film title'], ['genre', 'Genre'], ['language', 'Language'],
    ['synopsis', 'Synopsis'], ['film_link', 'Film link'],
    ['director_name', 'Director name'], ['director_phone', 'Phone number'], ['director_email', 'Contact email'],
  ];

  const durationHrs = parseInt(form.duration_hours, 10) || 0;
  const durationMins = parseInt(form.duration_mins, 10) || 0;
  const durationSecs = parseInt(form.duration_secs, 10) || 0;
  const durationTotalSeconds = durationHrs * 3600 + durationMins * 60 + durationSecs;
  const durationDisplay = durationTotalSeconds > 0
    ? [durationHrs ? `${durationHrs}h` : null, durationMins ? `${durationMins}m` : null, durationSecs ? `${durationSecs}s` : null].filter(Boolean).join(' ')
    : '';

  const handleReview = (e) => {
    e.preventDefault();
    const errs = {};
    const missing = REQUIRED_FIELDS.filter(([key]) => !form[key] || !String(form[key]).trim());
    if (durationTotalSeconds <= 0) missing.push(['duration', 'Duration']);
    if ((form.format === 'series' || form.format === 'vertical') && !(parseInt(form.episode_count, 10) > 0)) missing.push(['episode_count', 'Number of Episodes']);
    if (missing.length > 0) {
      return setError(`Please fill in all required fields — missing: ${missing.map(([, label]) => label).join(', ')}.`);
    }
    if (!isAllowedLink(form.film_link)) errs.film_link = 'Must be a Google Drive, YouTube, or Vimeo link';
    if (form.trailer_link && !isAllowedLink(form.trailer_link)) errs.trailer_link = 'Must be a Google Drive, YouTube, or Vimeo link';
    if (!/^\S+@\S+\.\S+$/.test(form.director_email.trim())) errs.director_email = 'Enter a valid email address';
    if (form.director_phone.replace(/\D/g, '').length < 10) errs.director_phone = 'Enter a valid phone number';
    if (Object.keys(errs).length > 0) { setFieldErrors(errs); return setError('Please fix the highlighted fields before continuing.'); }
    if (!editId && !agreed) return setError('You must agree to the content terms to submit.');
    setFieldErrors({}); setError('');
    setStep('review');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleConfirm = async () => {
    // Defensive re-check in case form state was mutated between steps.
    if (!isAllowedLink(form.film_link) || (form.trailer_link && !isAllowedLink(form.trailer_link))) {
      setStep('form');
      return setError('Please fix the highlighted links before continuing.');
    }
    setLoading(true); setError('');
    try {
      if (editId) {
        await API.put(`/api/submissions/${editId}`, form);
      } else {
        await API.post('/api/submissions', { ...form, is_resubmission: !!resubmitId, original_submission_id: resubmitId || null, is_general_submission: submitToGeneral });
      }
      navigate('/success', { state: { title: form.title, isEdit: !!editId } });
    } catch (err) {
      setError(err.response?.data?.error || 'Submission failed. Please try again.');
      setStep('form');
    } finally { setLoading(false); }
  };

  const spotsLeft = (seasonInfo && seasonInfo.limitEnabled !== false) ? seasonInfo.limit - seasonInfo.count : null;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      <Navbar />
      <div style={{ maxWidth: 700, margin: '0 auto', padding: '40px 24px' }}>

        {/* Header */}
        <div style={{ marginBottom: 36 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: 'var(--pink)', letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 10 }}>
              {resubmitId ? 'Resubmission' : editId ? 'Edit Submission' : 'New Submission'}
            </div>
            <button type="button" onClick={() => navigate('/how-to-submit')} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font)', textDecoration: 'underline', whiteSpace: 'nowrap' }}>
              Need help? →
            </button>
          </div>
          <h1 style={{ fontWeight: 900, fontSize: 34, color: 'var(--text)', marginBottom: 8, letterSpacing: '-0.02em' }}>
            {editId ? 'Edit your film' : resubmitId ? 'Resubmit your film' : 'Submit your film'}
          </h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: 14, fontWeight: 400 }}>
            {seasonInfo?.season
              ? `${seasonInfo.season.name} · Closes ${new Date(seasonInfo.season.end_date).toLocaleDateString('en-IN', { day:'numeric', month:'long', year:'numeric' })}`
              : 'Open submissions · Accepting films year-round'}
          </p>
        </div>

        {/* When a season is running, let the filmmaker choose between the
            season's own review batch and the always-open general pool —
            some films don't fit a season's timing/theme and shouldn't be
            forced into it. */}
        {!editId && seasonInfo?.season && (
          <div style={{ marginBottom: 28 }}>
            <div style={{ display: 'flex', gap: 10, marginBottom: 8 }}>
              {[[false, seasonInfo.season.name], [true, 'General Submission']].map(([val, label]) => (
                <button key={String(val)} type="button" onClick={() => setSubmitToGeneral(val)}
                  style={{ flex: 1, padding: '12px', borderRadius: 'var(--r-md)', fontSize: 12, fontWeight: 700, border: `1.5px solid ${submitToGeneral === val ? 'var(--pink)' : 'var(--border)'}`, background: submitToGeneral === val ? 'var(--pink-glow)' : 'var(--bg-elevated)', color: submitToGeneral === val ? 'var(--pink)' : 'var(--text-secondary)', cursor: 'pointer', fontFamily: 'var(--font)' }}>
                  {label}
                </button>
              ))}
            </div>
            <p style={{ color: 'var(--text-muted)', fontSize: 12, fontWeight: 400 }}>
              {submitToGeneral
                ? "Your film will be reviewed in Shortflix's year-round general pool, outside this season's batch."
                : `Your film will be reviewed as part of ${seasonInfo.season.name}.`}
            </p>
          </div>
        )}

        {/* Alerts */}
        {resubmitId && (
          <div style={alertStyle('var(--purple)', 'rgba(155,93,229,0.08)', 'rgba(155,93,229,0.25)')}>
            <AlertCircle size={14} color="var(--purple)" />
            You're resubmitting. Make sure you've addressed the feedback before submitting again.
          </div>
        )}
        {spotsLeft !== null && spotsLeft < 20 && !submitToGeneral && (
          <div style={alertStyle('var(--amber)', 'rgba(255,181,71,0.08)', 'rgba(255,181,71,0.25)')}>
            <AlertCircle size={14} color="var(--amber)" />
            Only {spotsLeft} spots remaining this season. Submit soon!
          </div>
        )}
        {error && (
          <div style={alertStyle('var(--red)', 'rgba(255,68,68,0.08)', 'rgba(255,68,68,0.25)')}>
            <AlertCircle size={14} color="var(--red)" />
            {error}
          </div>
        )}

        {step === 'form' && (
        <form onSubmit={handleReview}>
          {/* Film Details */}
          <Section icon={<Film size={16} />} title="Film Details" subtitle="Tell us about your film">
            <Field label="Film Title" required>
              <input value={form.title} onChange={e => set('title', e.target.value)} placeholder="Enter the full title of your film" />
            </Field>
            <div className="submit-field-grid">
              <Field label="Genre" required>
                <select value={form.genre} onChange={e => set('genre', e.target.value)}>
                  <option value="">Select genre</option>
                  {genres.map(g => <option key={g} value={g}>{g}</option>)}
                </select>
              </Field>
              <Field label="Language" required>
                <select value={form.language} onChange={e => set('language', e.target.value)}>
                  <option value="">Select language</option>
                  {LANGUAGES.map(l => <option key={l} value={l}>{l}</option>)}
                </select>
              </Field>
            </div>
            <Field label="Format" required hint="Is your film shot/edited for horizontal (widescreen) or vertical (mobile) viewing, or is this a series?">
              <div style={{ display: 'flex', gap: 10 }}>
                {['horizontal', 'vertical', 'series'].map(f => (
                  <button
                    key={f}
                    type="button"
                    onClick={() => set('format', f)}
                    style={{
                      flex: 1, padding: '10px 14px', borderRadius: 8, cursor: 'pointer',
                      border: form.format === f ? '1.5px solid var(--pink)' : '1.5px solid var(--border)',
                      background: form.format === f ? 'var(--pink-glow)' : 'var(--bg-card)',
                      color: form.format === f ? 'var(--pink)' : 'var(--text-muted)',
                      fontWeight: 700, fontSize: 13, textTransform: 'capitalize',
                    }}
                  >
                    {f}
                  </button>
                ))}
              </div>
            </Field>
            {(form.format === 'series' || form.format === 'vertical') && (
              <div style={{ display: 'flex', gap: 10 }}>
                <div style={{ flex: 1 }}>
                  <Field label="Number of Episodes" required>
                    <input type="number" min="1" value={form.episode_count} onChange={e => set('episode_count', e.target.value)} placeholder="e.g. 8" />
                  </Field>
                </div>
                <div style={{ flex: 1 }}>
                  <Field label="Season (optional)">
                    <input type="number" min="1" value={form.season_number} onChange={e => set('season_number', e.target.value)} placeholder="e.g. 1" />
                  </Field>
                </div>
              </div>
            )}
            <Field label="Duration" required hint="No runtime limit — most short films only need Minutes and Seconds, leave Hours at 0">
              <div style={{ display: 'flex', gap: 10 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Hours</div>
                  <input type="number" min="0" value={form.duration_hours} onChange={e => set('duration_hours', e.target.value)} placeholder="0" />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Minutes</div>
                  <input type="number" min="0" max="59" value={form.duration_mins} onChange={e => set('duration_mins', e.target.value)} placeholder="0" />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Seconds</div>
                  <input type="number" min="0" max="59" value={form.duration_secs} onChange={e => set('duration_secs', e.target.value)} placeholder="0" />
                </div>
              </div>
              {durationTotalSeconds > 0 && (
                <p style={{ fontSize: 12, color: durationTotalSeconds > 14400 ? 'var(--amber)' : 'var(--text-muted)', fontWeight: 600, marginTop: 8 }}>
                  Runtime entered: {durationDisplay}
                  {durationTotalSeconds > 14400 && ' — that\'s over 4 hours, please double-check this is correct.'}
                </p>
              )}
            </Field>
            <Field label="Synopsis" required hint="3–5 sentences about your film. Helps reviewers understand context before watching.">
              <textarea value={form.synopsis} onChange={e => set('synopsis', e.target.value)} placeholder="What is your film about?" rows={4} />
            </Field>
            <Field label="Notes to Reviewers" hint="Optional — anything specific you'd like reviewers to keep in mind while watching.">
              <textarea value={form.notes_to_reviewers} onChange={e => set('notes_to_reviewers', e.target.value)} placeholder="e.g. This is a silent film. The audio track begins at 2 min 30 sec. Shot on iPhone..." rows={3} />
            </Field>
          </Section>

          {/* Links */}
          <Section icon={<Link2 size={16} />} title="Film Links" subtitle="Provide accessible links to your film">
            <Field label="Film Link" required hint="Google Drive, Vimeo, or YouTube. Set to 'Anyone with the link can view'.">
              <input value={form.film_link} onChange={e => { set('film_link', e.target.value); setFieldErrors(fe => ({ ...fe, film_link: '' })); }} placeholder="https://drive.google.com/..." type="url" />
              {fieldErrors.film_link && <p style={{ fontSize: 11, color: 'var(--red)', marginTop: 5, fontWeight: 600 }}>{fieldErrors.film_link}</p>}
            </Field>
            <Field label="Film Link Password" hint="Optional — only if your link is password-protected (e.g. a private Vimeo password).">
              <input value={form.film_link_password} onChange={e => set('film_link_password', e.target.value)} placeholder="Leave blank if not password-protected" type="text" />
            </Field>
            <Field label="Trailer Link" hint="Optional — a shorter preview clip if available.">
              <input value={form.trailer_link} onChange={e => { set('trailer_link', e.target.value); setFieldErrors(fe => ({ ...fe, trailer_link: '' })); }} placeholder="https://youtube.com/..." type="url" />
              {fieldErrors.trailer_link && <p style={{ fontSize: 11, color: 'var(--red)', marginTop: 5, fontWeight: 600 }}>{fieldErrors.trailer_link}</p>}
            </Field>
          </Section>

          {/* Director */}
          <Section icon={<User size={16} />} title="Director Details" subtitle="Visible only to the Shortflix admin team — never shared with reviewers">
            <Field label="Director Name" required>
              <input value={form.director_name} onChange={e => set('director_name', e.target.value)} placeholder="Full name of the director" />
            </Field>
            <div className="submit-field-grid">
              <Field label="Phone Number" required>
                <input value={form.director_phone} onChange={e => { set('director_phone', e.target.value); setFieldErrors(fe => ({ ...fe, director_phone: '' })); }} placeholder="+91 XXXXX XXXXX" />
                {fieldErrors.director_phone && <p style={{ fontSize: 11, color: 'var(--red)', marginTop: 5, fontWeight: 600 }}>{fieldErrors.director_phone}</p>}
              </Field>
              <Field label="Contact Email" required>
                <input value={form.director_email} onChange={e => { set('director_email', e.target.value); setFieldErrors(fe => ({ ...fe, director_email: '' })); }} placeholder="director@email.com" type="email" />
                {fieldErrors.director_email && <p style={{ fontSize: 11, color: 'var(--red)', marginTop: 5, fontWeight: 600 }}>{fieldErrors.director_email}</p>}
              </Field>
            </div>
            <Field label="Studio" hint="Production studio/banner, if this film belongs to one. Optional.">
              <input value={form.studio_name} onChange={e => set('studio_name', e.target.value)} placeholder="e.g. Backpacker Films Pvt Ltd" />
            </Field>
          </Section>

          {/* Terms — only on new submission */}
          {!editId && (
            <Section icon={<FileText size={16} />} title="Content Agreement" subtitle="Please read and agree before submitting">
              <div className="card" style={{ padding: 20, marginBottom: 20, background: 'var(--bg-elevated)' }}>
                <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.8, fontWeight: 400 }}>
                  <p style={{ marginBottom: 12 }}>
                    By submitting this film, you confirm and agree to the following:
                  </p>
                  <ul style={{ paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <li><strong style={{ color: 'var(--text)' }}>Ownership:</strong> You are the original creator and own all rights to this film, its content, music, and visuals. If any third-party content is used, you have obtained the necessary permissions.</li>
                    <li><strong style={{ color: 'var(--text)' }}>Review Permission:</strong> You grant Shortflix a limited, non-exclusive right to screen and evaluate this film internally for the sole purpose of the review and selection process.</li>
                    <li><strong style={{ color: 'var(--text)' }}>No Guarantee:</strong> Submission does not guarantee selection, approval, or streaming on the Shortflix platform. Our team evaluates each film independently.</li>
                    <li><strong style={{ color: 'var(--text)' }}>Streaming Rights (Post-Approval Only):</strong> If your film is approved for streaming, a separate Content License Agreement will be shared with you before any content goes live. All streaming terms, revenue arrangements, and exclusivity clauses will be discussed and agreed upon at that stage — nothing is assumed from submission alone.</li>
                    <li><strong style={{ color: 'var(--text)' }}>Confidentiality:</strong> Your personal contact details will never be shared with reviewers. The review process is anonymous to protect both parties.</li>
                    <li><strong style={{ color: 'var(--text)' }}>Originality:</strong> The film has not been plagiarised. You take full responsibility for any copyright disputes arising from the content submitted.</li>
                  </ul>
                </div>
              </div>

              <label style={{ display: 'flex', gap: 12, alignItems: 'flex-start', cursor: 'pointer' }}>
                <div style={{ position: 'relative', flexShrink: 0, marginTop: 1 }}>
                  <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)}
                    style={{ width: 18, height: 18, accentColor: 'var(--pink)', cursor: 'pointer', flexShrink: 0 }} />
                </div>
                <span style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6, fontWeight: 500 }}>
                  I have read and agree to the above terms. I confirm I own all rights to this film and understand that streaming rights will only be discussed upon approval — submission is for review purposes only.
                </span>
              </label>

              {agreed && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, color: 'var(--green)', fontSize: 12, fontWeight: 600 }}>
                  <CheckCircle size={14} color="var(--green)" /> Agreement confirmed
                </div>
              )}
            </Section>
          )}

          <div style={{ display: 'flex', gap: 12 }}>
            <button type="button" onClick={() => navigate('/dashboard')} className="btn-ghost" style={{ flex: 1 }}>Cancel</button>
            <button type="submit" className="btn-primary" style={{ flex: 2, padding: '13px', fontSize: 13 }}>
              {editId ? 'Review Changes' : 'Review & Submit'}
            </button>
          </div>
        </form>
        )}

        {step === 'review' && (
          <div>
            <Section icon={<CheckCircle size={16} />} title="Review Your Submission" subtitle="Check everything below before confirming — you can still go back and edit.">
              {[
                ['Film Title', form.title],
                ['Genre', form.genre],
                ['Language', form.language],
                ['Format', form.format === 'vertical' ? 'Vertical' : form.format === 'series' ? 'Series' : 'Horizontal'],
                ...((form.format === 'series' || form.format === 'vertical') ? [['Number of Episodes', form.episode_count], ['Season', form.season_number]] : []),
                ['Duration', durationDisplay],
                ['Synopsis', form.synopsis],
                ['Notes to Reviewers', form.notes_to_reviewers],
              ].filter(([, v]) => v).map(([label, value]) => (
                <div key={label} style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 3 }}>{label}</div>
                  <div style={{ fontSize: 14, color: 'var(--text)', fontWeight: 500, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{value}</div>
                </div>
              ))}
            </Section>

            <Section icon={<Link2 size={16} />} title="Links">
              {[
                ['Film Link', form.film_link],
                ['Film Link Password', form.film_link_password],
                ['Trailer Link', form.trailer_link],
              ].filter(([, v]) => v).map(([label, value]) => (
                <div key={label} style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 3 }}>{label}</div>
                  <div style={{ fontSize: 14, color: 'var(--text)', fontWeight: 500, wordBreak: 'break-all' }}>{value}</div>
                </div>
              ))}
            </Section>

            <Section icon={<User size={16} />} title="Director Details">
              {[
                ['Director Name', form.director_name],
                ['Phone Number', form.director_phone],
                ['Contact Email', form.director_email],
                ['Studio', form.studio_name],
              ].filter(([, v]) => v).map(([label, value]) => (
                <div key={label} style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 3 }}>{label}</div>
                  <div style={{ fontSize: 14, color: 'var(--text)', fontWeight: 500 }}>{value}</div>
                </div>
              ))}
            </Section>

            <div style={{ display: 'flex', gap: 12 }}>
              <button type="button" onClick={() => setStep('form')} className="btn-ghost" style={{ flex: 1 }}>← Back to Edit</button>
              <button type="button" onClick={handleConfirm} disabled={loading} className="btn-primary" style={{ flex: 2, padding: '13px', fontSize: 13 }}>
                {loading ? 'Submitting...' : editId ? 'Confirm Changes' : 'Confirm & Submit'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function alertStyle(color, bg, border) {
  return { background: bg, border: `1.5px solid ${border}`, borderRadius: 'var(--r-md)', padding: '11px 14px', marginBottom: 16, fontSize: 13, color, display: 'flex', alignItems: 'center', gap: 8, fontWeight: 500 };
}
